// TextAreaControl.tsx
import React, { useEffect, useState } from 'react';
import { Control } from '../ControlRenderer';
import { get, set } from '../utils/Utils'
import { FormControl, TextareaAutosize, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FormModeType } from '@/components/FormDataProvider';
import { useValidation } from '@/components/ValidationProvider';
import ControlIcons from '../ControlIcons';

interface TextAreaControl extends Control {
  placeholder?: string;

  /**  * Whether to hide the tooltip for the text.
   */
  hideTooltip?: boolean;
}

interface TextAreaControlProps {
  control: Control;
  data: any;
  onDataChange: (newData: any) => void;
  mode: FormModeType;
}

const TextAreaControl: React.FC<TextAreaControlProps> = ({ control, data, onDataChange, mode }) => {
  const { errors, warnings, validateData, setDataContext } = useValidation();
  const theme = useTheme();

  const handleFieldChange = (fieldName: string, value: string) => {
    setSelectedValue(value);
    set(data, fieldName, value);
    onDataChange(data);
  };

  const textStyle: React.CSSProperties = {
  };

  if (control.horizontalAlignment === 'stretch') {
    textStyle.width = '100%';
  }

  if (control.horizontalAlignment === 'stretch') {
    textStyle.width = '100%';
  }

  if (control.minWidth) {
    textStyle.minWidth = control.minWidth;
  }

  const [selectedValue, setSelectedValue] = useState<any>(get(data, control.dataKey ?? '', '') ?? '');

  const [isFocused, setIsFocused] = useState(false);

  const errorMessage = errors && control.dataKey ? errors[control.dataKey] : undefined;
  const warningMessage = warnings && control.dataKey ? warnings[control.dataKey] : undefined;

  const hasControlIcons = !!control.helpText || !!control.helpLink || !!errorMessage || !!warningMessage;

  const textAreaControl = control as TextAreaControl;

  useEffect(() => {
    setSelectedValue(get(data, control.dataKey ?? '', '') ?? '')
  }, [data, control.dataKey]);

  useEffect(() => {
    if (data) {
      setDataContext(data);
    }
  }, [data, setDataContext]);

  let currentMode = control.mode ? control.mode : mode;

  if (currentMode === 'label') {
    return (
      <Typography className='eform-labelmode'>
        {get(data, control.dataKey ?? '', '')}
      </Typography>
    )
  }

  const className = `eform-text-${currentMode === 'view' ? 'readonly-' : ''}${theme.palette.mode}`;

  const hideTooltip = textAreaControl.hideTooltip ?? false;

  return (
    <Tooltip
      title={selectedValue && !isFocused && !hideTooltip ? selectedValue.toString() : ''}
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -10], // move 8px closer to the item
            },
          },
        ],
      }}
      arrow>
      {hasControlIcons ? (
        <FormControl variant="standard" style={textStyle} error={!!errorMessage}><TextareaAutosize
          minRows={3}
          className={className}
          style={textStyle}
          name={control.name}
          value={selectedValue}
          placeholder={textAreaControl.placeholder}
          onFocus={() => setIsFocused(true)}
          onBlur={() => { setIsFocused(false); validateData(); }}
          onChange={(e) => handleFieldChange(control.dataKey ?? '', e.target.value)}
          readOnly={currentMode === 'view'}
          spellCheck={true}
        />
        <ControlIcons control={control} errorMessage={errorMessage} warningMessage={warningMessage} />
        </FormControl>
      ) : (
        <TextareaAutosize
          minRows={3}
          className={className}
          style={textStyle}
          name={control.name}
          value={selectedValue}
          placeholder={textAreaControl.placeholder}
          onFocus={() => setIsFocused(true)}
          onBlur={() => { setIsFocused(false); validateData(); }}
          onChange={(e) => handleFieldChange(control.dataKey ?? '', e.target.value)}
          readOnly={currentMode === 'view'}
          spellCheck={true}
        />
      )}
    </Tooltip>
  );
};

export default TextAreaControl;
